import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  TrendingDown as UndervalIcon,
  TrendingUp as OvervalIcon,
  ErrorOutline as ErrorIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useEffectiveClientScope } from "@/hooks";
import {
  useGetValueDiscrepanciesQuery,
  useGetClassificationDiscrepanciesQuery,
  useUpdateDiscrepancyReviewStatusMutation,
} from "@/services/customs-document/customs-document-api";
import { EnhancedReconciliationModal } from "@/components";
import { useSnackbar } from "@/hooks";
import VerificationToolbar from "./verification-toolbar";
import DiscrepancyDetailDialog from "./discrepancy-detail-dialog";
import ClassificationDetailDialog from "./classification-detail-dialog";
import type { ReviewStatus } from "./discrepancy-review-controls";
import { formatCurrency, formatCurrencyPrecise, getItemKey, getClassificationItemKey, COLUMNS } from "./verification-helpers";
import ClassificationDutyTab from "./ClassificationDutyTab";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";
import StatsBar from "@/components/stats-bar";
import SegmentedTabs from "@/components/segmented-tabs";
import SeverityDot from "@/components/severity-dot";
import Pagination from "@/components/paginations/pagination";
import { tokens } from "@/tokens";

const INITIAL_PAGE = 1;
const DEFAULT_LIMIT = 25;

const FILTER_TABS = [
  { id: "valuation", label: "Valuation" },
  { id: "classification", label: "Classification" },
  { id: "origin", label: "Origin" },
];

const ComingSoonPlaceholder: React.FC<{ category: string; icon: React.ReactNode }> = ({ category, icon }) => (
  <Box sx={{ ...tableContainerSx, py: 8 }}>
    <Stack alignItems="center" spacing={2}>
      <Box sx={{ p: 2, bgcolor: tokens.bg.muted, borderRadius: 2 }}>
        {icon}
      </Box>
      <Typography variant="h6" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary }}>
        {category} Verification
      </Typography>
      <Chip
        icon={<LockIcon sx={{ fontSize: 14 }} />}
        label="Coming Soon"
        size="small"
        sx={{
          bgcolor: tokens.bg.muted,
          color: tokens.text.secondary,
          fontWeight: tokens.fontWeight.bold,
          fontSize: tokens.fontSize.base,
        }}
      />
      <Typography variant="body2" sx={{ color: tokens.text.secondary, maxWidth: 400, textAlign: 'center' }}>
        Automated {category.toLowerCase()} verification is currently in development.
        This feature will flag discrepancies in your entries for review.
      </Typography>
    </Stack>
  </Box>
);

const VerificationTab: React.FC = () => {
  const { clientId, tenantId, isTDAdmin, shouldRequireClientSelection } =
    useEffectiveClientScope();
  const { show: showSnackbar } = useSnackbar();
  const [filterType, setFilterType] = useState<string>("valuation");
  const [page, setPage] = useState(INITIAL_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  // Selection state
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Detail dialog state — separate open flag so Drawer can animate out
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<ValueDiscrepancyItem | null>(null);

  // Classification detail dialog state — separate open flag so Drawer can animate out
  const [classificationDetailOpen, setClassificationDetailOpen] = useState(false);
  const [classificationDetailItem, setClassificationDetailItem] = useState<ClassificationDiscrepancyItem | null>(null);

  // Adjustment dialog state
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);

  // API mutation for bulk review status
  const [updateReviewStatus] = useUpdateDiscrepancyReviewStatusMutation();

  const { data, isLoading, isFetching } = useGetValueDiscrepanciesQuery(
    {
      clientId,
      ...(isTDAdmin && tenantId && { tenantId }),
      pageNo: page,
      pageSize: limit,
    },
    { skip: shouldRequireClientSelection || filterType !== "valuation" }
  );

  // HTS Classification discrepancies query (shown in Classification tab)
  const { data: classificationData, isLoading: classificationLoading, isFetching: classificationFetching } = useGetClassificationDiscrepanciesQuery(
    {
      clientId,
      ...(isTDAdmin && tenantId && { tenantId }),
      pageNo: page,
      pageSize: limit,
    },
    { skip: shouldRequireClientSelection || filterType !== "classification" }
  );

  const items = data?.data || [];
  const totals = data?.totals;
  const countDocs = data?.countDocs || 0;

  // Classification items
  const classificationItems = classificationData?.data || [];
  const classificationTotals = classificationData?.totals;
  const classificationCountDocs = classificationData?.countDocs || 0;

  // Derive visible item keys for select-all
  const visibleKeys = useMemo(
    () => items.map(getItemKey),
    [items]
  );

  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.has(k));
  const someVisibleSelected = visibleKeys.some((k) => selectedKeys.has(k));

  // Selected items and entry lines for adjustment dialog
  const selectedItems = useMemo(
    () => items.filter((item) => selectedKeys.has(getItemKey(item))),
    [items, selectedKeys]
  );

  // Build entry lines for adjustment modal
  const selectedEntryLines = useMemo(
    () => {
      const entryLines: string[] = [];
      for (const item of selectedItems) {
        if (item.entryLines && item.entryLines.length > 0) {
          entryLines.push(...item.entryLines);
        } else if (item.entryLine) {
          entryLines.push(item.entryLine);
        } else if (item.entryNumber) {
          if (item.lineNumbers && item.lineNumbers.length > 0) {
            for (const ln of item.lineNumbers) {
              entryLines.push(`${item.entryNumber.replace(/-/g, '')}-${String(ln).padStart(2, '0')}`);
            }
          } else {
            const lineNum = item.lineNumber ?? 1;
            entryLines.push(`${item.entryNumber.replace(/-/g, '')}-${String(lineNum).padStart(2, '0')}`);
          }
        }
      }
      return [...new Set(entryLines)];
    },
    [selectedItems]
  );

  const handleFilterChange = (newFilter: string) => {
    setFilterType(newFilter);
    setPage(INITIAL_PAGE);
    setSelectedKeys(new Set());
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedKeys(new Set());
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(INITIAL_PAGE);
    setSelectedKeys(new Set());
  };

  // Selection handlers
  const handleSelectAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(visibleKeys));
    }
  }, [allVisibleSelected, visibleKeys]);

  const handleSelectRow = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Bulk update review status via API
  const handleBulkReview = useCallback(
    async (status: ReviewStatus) => {
      const selectedItemsList = items.filter((item) => selectedKeys.has(getItemKey(item)));
      setSelectedKeys(new Set());

      try {
        await Promise.all(
          selectedItemsList.map((item) =>
            updateReviewStatus({
              auditId: item.auditId,
              discrepancyId: item.discrepancyId,
              status,
              ...(isTDAdmin && tenantId && { tenantId }),
            }).unwrap()
          )
        );
      } catch {
        showSnackbar('Error', 'Failed to update some items', 'error');
      }
    },
    [items, selectedKeys, updateReviewStatus, isTDAdmin, tenantId, showSnackbar]
  );

  const handleClassificationBulkReview = useCallback(
    async (status: ReviewStatus) => {
      const selectedItemsList = classificationItems.filter((item) => selectedKeys.has(getClassificationItemKey(item)));
      setSelectedKeys(new Set());

      try {
        await Promise.all(
          selectedItemsList.map((item) =>
            updateReviewStatus({
              auditId: item.auditId,
              discrepancyId: item.discrepancyId,
              status,
              ...(isTDAdmin && tenantId && { tenantId }),
            }).unwrap()
          )
        );
      } catch {
        showSnackbar('Error', 'Failed to update some items', 'error');
      }
    },
    [classificationItems, selectedKeys, updateReviewStatus, isTDAdmin, tenantId, showSnackbar]
  );

  // Selected classification items and entry lines for adjustment dialog
  const selectedClassificationItems = useMemo(
    () => classificationItems.filter((item) => selectedKeys.has(getClassificationItemKey(item))),
    [classificationItems, selectedKeys]
  );

  const selectedClassificationEntryLines = useMemo(
    () => {
      const entryLines: string[] = [];
      for (const item of selectedClassificationItems) {
        if (item.entryLines && item.entryLines.length > 0) {
          entryLines.push(...item.entryLines);
        } else if (item.entryLine) {
          entryLines.push(item.entryLine);
        } else if (item.entryNumber) {
          if (item.lineNumbers && item.lineNumbers.length > 0) {
            for (const ln of item.lineNumbers) {
              entryLines.push(`${item.entryNumber.replace(/-/g, '')}-${String(ln).padStart(2, '0')}`);
            }
          } else {
            const lineNum = item.lineNumber ?? 1;
            entryLines.push(`${item.entryNumber.replace(/-/g, '')}-${String(lineNum).padStart(2, '0')}`);
          }
        }
      }
      return [...new Set(entryLines)];
    },
    [selectedClassificationItems]
  );

  return (
    <Stack sx={{
      height: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      flex: 1,
    }}>
      {/* Sub-tabs + stats row */}
      <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, pl: 3, pr: 3, pt: 2, pb: 2, gap: 3 }}>
        <SegmentedTabs
          tabs={FILTER_TABS}
          activeTab={filterType}
          onChange={handleFilterChange}
        />

        {/* Stats */}
        {filterType === "valuation" && (
          <Box sx={{ ml: 'auto', mr: 2 }}>
            <StatsBar
              isLoading={isLoading}
              compact
              items={[
                { label: "Discrepancies", value: totals?.count ?? 0, centerValue: true },
                { label: "Value Exposure", value: formatCurrency(totals?.totalDiscrepancyAmount), color: tokens.text.primary },
                { label: "Add'l Duty", value: formatCurrency(totals?.totalEstimatedDutiesOwed), color: tokens.text.primary },
                { label: "Recoverable", value: formatCurrency(totals?.totalEstimatedDutiesSaved), color: tokens.status.success },
              ]}
            />
          </Box>
        )}
        {filterType === "classification" && (
          <Box sx={{ ml: 'auto', mr: 2 }}>
            <StatsBar
              isLoading={classificationLoading}
              compact
              items={[
                { label: "Classification Issues", value: classificationTotals?.count ?? 0, centerValue: true },
                { label: "Add'l Duty", value: formatCurrency(classificationTotals?.dutiesOwed), color: tokens.text.primary },
                { label: "Recoverable", value: formatCurrency(classificationTotals?.dutiesSaved), color: tokens.status.success },
              ]}
            />
          </Box>
        )}
      </Stack>

      {/* Classification / Duty Discrepancies */}
      {filterType === "classification" && (
        <>
          <VerificationToolbar
            selectedCount={selectedKeys.size}
            onClear={() => setSelectedKeys(new Set())}
            onMarkConfirmed={() => handleClassificationBulkReview('confirmed')}
            onMarkNotAnIssue={() => handleClassificationBulkReview('not_an_issue')}
            onCreateAdjustment={() => setAdjustmentOpen(true)}
          />
          <ClassificationDutyTab
            items={classificationItems}
            countDocs={classificationCountDocs}
            isLoading={classificationLoading}
            isFetching={classificationFetching}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            page={page}
            limit={limit}
            handlePageChange={handlePageChange}
            handleLimitChange={handleLimitChange}
            onItemClick={(item) => { setClassificationDetailItem(item); setClassificationDetailOpen(true); }}
          />
        </>
      )}

      {/* Origin - Coming Soon */}
      {filterType === "origin" && (
        <Box sx={{ px: 3, pt: 1 }}>
          <ComingSoonPlaceholder
            category="Country of Origin"
            icon={<Box sx={{ fontSize: 40, color: '#8B5CF6' }}>🌍</Box>}
          />
        </Box>
      )}

      {/* Valuation - Real Data */}
      {filterType === "valuation" && (
        <>
          {/* Selection Toolbar */}
          <VerificationToolbar
            selectedCount={selectedKeys.size}
            onClear={() => setSelectedKeys(new Set())}
            onMarkConfirmed={() => handleBulkReview('confirmed')}
            onMarkNotAnIssue={() => handleBulkReview('not_an_issue')}
            onCreateAdjustment={() => setAdjustmentOpen(true)}
          />

          {/* Table Section */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}>
            <Stack sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              mx: 3,
              mb: 2,
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.default}`,
              bgcolor: tokens.bg.surface,
              overflow: "hidden",
              ...tableCompactSx,
            }}>
              {isLoading ? (
                <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "12px" }} />
              ) : (
                <>
                  <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
                    {isFetching && !isLoading && (
                      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {COLUMNS.map((col) => (
                            <TableCell
                              key={col.key}
                              align={col.align || "left"}
                              padding={col.padding || "normal"}
                              sx={{
                                ...(col.key === 'checkbox' && { width: 36 }),
                                ...(col.padding === 'checkbox' && { padding: "0 0 0 8px !important" }),
                              }}
                            >
                              {col.key === 'checkbox' ? (
                                <Checkbox
                                  size="small"
                                  checked={allVisibleSelected}
                                  indeterminate={someVisibleSelected && !allVisibleSelected}
                                  onChange={handleSelectAll}
                                  sx={{
                                    color: tokens.text.subtle,
                                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens.text.primary },
                                  }}
                                />
                              ) : (
                                col.label
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item, idx) => {
                          const itemKey = getItemKey(item);
                          const isSelected = selectedKeys.has(itemKey);
                          const reviewStatus = item.reviewStatus;
                          const reviewStatusSetBy = item.reviewStatusSetBy;
                          const isUnderval = item.discrepancyType === 'UNDERVALUATION';
                          return (
                            <TableRow
                              key={`${item.auditId}-${idx}`}
                              onClick={() => { setDetailItem(item); setDetailOpen(true); }}
                              sx={{
                                cursor: "pointer",
                                backgroundColor: isSelected ? tokens.bg.muted : tokens.bg.surface,
                                "&:hover": {
                                  backgroundColor: isSelected ? tokens.bg.border : tokens.bg.hover,
                                },
                              }}
                            >
                              {/* Checkbox */}
                              <TableCell padding="checkbox" sx={{ pl: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => handleSelectRow(itemKey)}
                                  sx={{
                                    color: tokens.text.subtle,
                                    '&.Mui-checked': { color: tokens.text.primary },
                                  }}
                                />
                              </TableCell>
                              {/* Entry # */}
                              <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontFamily: "monospace", fontSize: tokens.fontSize.sm2 }}>
                                {item.entryNumber || '-'}
                              </TableCell>
                              {/* Entry Date */}
                              <TableCell sx={{ color: tokens.text.secondary, whiteSpace: "nowrap" }}>
                                {item.entryDate
                                  ? new Date(item.entryDate).toLocaleDateString('en-US', {
                                      month: 'short', day: 'numeric', year: 'numeric',
                                    })
                                  : '-'}
                              </TableCell>
                              {/* Severity */}
                              <TableCell>
                                <SeverityDot severity={item.severity?.toLowerCase() as "high" | "medium" | "low"} />
                              </TableCell>
                              {/* Title — with colored arrow */}
                              <TableCell sx={{ maxWidth: 260 }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                  {isUnderval ? (
                                    <UndervalIcon sx={{ fontSize: 16, color: tokens.status.danger, mt: 0.25, flexShrink: 0 }} />
                                  ) : (
                                    <OvervalIcon sx={{ fontSize: 16, color: tokens.status.success, mt: 0.25, flexShrink: 0 }} />
                                  )}
                                  <Tooltip title={`${item.title} — ${item.description}`} arrow placement="top-start">
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography noWrap sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2, maxWidth: 220 }}>
                                        {item.title}
                                      </Typography>
                                      <Typography noWrap sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontWeight: tokens.fontWeight.normal, maxWidth: 220 }}>
                                        {item.description}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              {/* Declared */}
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography sx={{ fontWeight: tokens.fontWeight.book, fontSize: tokens.fontSize.sm2 }}>
                                  {formatCurrencyPrecise(item.declaredValue)}
                                </Typography>
                              </TableCell>
                              {/* Discrepancy */}
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography
                                  sx={{
                                    fontWeight: tokens.fontWeight.medium,
                                    fontSize: tokens.fontSize.sm2,
                                    color: tokens.status.danger,
                                  }}
                                >
                                  {formatCurrencyPrecise(item.discrepancyAmount)}
                                </Typography>
                              </TableCell>
                              {/* Est. Duty */}
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                <Typography
                                  sx={{
                                    fontWeight: tokens.fontWeight.medium,
                                    fontSize: tokens.fontSize.sm2,
                                    color: tokens.status.success,
                                  }}
                                >
                                  {(item.estimatedDutyImpact ?? 0) > 0
                                    ? formatCurrencyPrecise(item.estimatedDutyImpact)
                                    : '-'}
                                </Typography>
                              </TableCell>
                              {/* Status */}
                              <TableCell>
                                {reviewStatus ? (
                                  <Box>
                                    <Chip
                                      label={reviewStatus === 'confirmed' ? 'Confirmed' : 'Not an Issue'}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        height: 18,
                                        fontSize: tokens.fontSize["2xs"],
                                        fontWeight: tokens.fontWeight.book,
                                        borderRadius: 0.5,
                                        bgcolor: 'transparent',
                                        ...(reviewStatus === 'confirmed'
                                          ? { color: tokens.status.danger, borderColor: tokens.status.danger }
                                          : { color: tokens.status.success, borderColor: tokens.status.success }),
                                      }}
                                    />
                                    {reviewStatusSetBy && (
                                      <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.25 }}>
                                        by {reviewStatusSetBy}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography sx={{ color: tokens.text.subtle, fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.normal }}>
                                    Pending
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 6 }}>
                              <Stack alignItems="center" spacing={2}>
                                <ErrorIcon sx={{ fontSize: 48, color: tokens.bg.border }} />
                                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                                  No value discrepancies found
                                </Typography>
                                <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted }}>
                                  Value discrepancies will appear here once audits identify valuation issues
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {countDocs > 0 && (
                    <Pagination
                      page={page}
                      limit={limit}
                      total={countDocs}
                      onPageChange={handlePageChange}
                      onLimitChange={handleLimitChange}
                    />
                  )}
                </>
              )}
            </Stack>
          </Box>
        </>
      )}

      {/* Detail Dialog */}
      <DiscrepancyDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        item={detailItem}
        onExited={() => setDetailItem(null)}
      />

      {/* Classification Detail Dialog */}
      <ClassificationDetailDialog
        open={classificationDetailOpen}
        onClose={() => setClassificationDetailOpen(false)}
        item={classificationDetailItem}
        onExited={() => setClassificationDetailItem(null)}
      />

      {/* Adjustment Modal */}
      <EnhancedReconciliationModal
        show={adjustmentOpen}
        setShow={setAdjustmentOpen}
        initialEntryLines={filterType === "classification" ? selectedClassificationEntryLines : selectedEntryLines}
        initialItems={filterType === "classification" ? undefined : selectedItems}
      />
    </Stack>
  );
};

export default VerificationTab;
